import InfoOutlined from "@mui/icons-material/InfoOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useCCMessages, useHealthCareProvider } from "../../api/useHcpProfileApi";
import { DataWindow, useDataWindows } from "../../api/useMetaDataApi";
import TableHeader from "../../components/Headers/TableHeader";
import AddToPrintQueueIcon from "../../components/Icons/AddToPrintQueueIcon";
import SimpleTable from "../../components/Table/SimpleTable";
import { Tab, TabPanel, useTabs } from "../../components/Tabs";
import Tooltip from "../../components/Tooltip/Tooltip";
import { useClientConfiguration } from "../../data/global/useProductConfiguration";
import { useHcpLandscapeState } from "../../data/hcpLandscape/hooks";
import useQueryParams from "../../hooks/useQueryParams";
import i18n from "../../i18n";
import { Column } from "../../components/Table/types";

// eslint-disable-next-line react-refresh/only-export-components
export enum CCTabNames {
  PRINT_MESSAGES = "print-messages",
  EMAIL_MESSAGES = "email-messages",
}

interface CCTableProps {
  columns: Column[];
  data: object[] | null | undefined;
  isLoading: boolean;
}

const CCTable: React.FC<CCTableProps> = ({ columns, data, isLoading }) => (
  <SimpleTable columns={columns} data={data} loading={isLoading} estimatedRows={4} />
);

const CostAndCoverageMessages: React.FC<{ defaultTab: CCTabNames }> = ({ defaultTab }) => {
  const [t] = useTranslation();
  const { client: selectedClient, id } = useParams() as { client: string; id: string };
  const hcpQuery = useHealthCareProvider(selectedClient, id);
  const { data: dataWindows } = useDataWindows(selectedClient);
  const { filters } = useHcpLandscapeState();
  const [initTab] = useQueryParams("tab", defaultTab);
  const config = useClientConfiguration(selectedClient);
  const tabs = useTabs(initTab);
  const defaultDataWindow =
    (dataWindows || []).find((dataWindow) => dataWindow === DataWindow.twelveMonths) || DataWindow.twelveMonths;
  const timePeriod = filters.timePeriod || defaultDataWindow;
  const { data, isLoading } = useCCMessages(
    selectedClient,
    id,
    timePeriod,
    hcpQuery.data,
    tabs.currentTab === CCTabNames.PRINT_MESSAGES,
  );

  const hcpMessageLink = "hcpMessageLink" as const;
  const columns: Column[] = [
    {
      label: t("hcpProfile.category"),
      align: "left",
      dataKey: "messageName",
      tdClasses: "sm:bg-light-blue",
      component: hcpMessageLink,
    },
    {
      label: t(`hcpProfile.repMessage.${selectedClient}`),
      align: "left",
      dataKey: "description",
    },
  ];

  const showTabs = config.print?.hcpPrintMessages && config.print.hcpEmailMessages;

  const hideCostCoverageTooltip = config.hcpProfile?.hideCostAndCoverageTooltip;

  return (
    <div className="pb-6">
      <TableHeader
        title={
          i18n.exists(`hcpProfile.costAndCoverageMessages.${selectedClient}`)
            ? t(`hcpProfile.costAndCoverageMessages.${selectedClient}`)
            : t(`hcpProfile.costAndCoverageMessages.default`)
        }
      >
        {!hideCostCoverageTooltip && (
          <Tooltip
            tip={t(
              `hcpProfile.costAndCoverageMessagesTooltip${
                tabs.currentTab === CCTabNames.PRINT_MESSAGES ? "Print" : "Email"
              }.${selectedClient}`,
            )}
          >
            <InfoOutlined className="text-primary" />
          </Tooltip>
        )}
      </TableHeader>
      {showTabs && (
        <>
          <div className="flex sm:border-b border-rules">
            <Tab tabName={CCTabNames.PRINT_MESSAGES} {...tabs}>
              <AddToPrintQueueIcon className="mr-1" /> {t("hcpProfile.costAndCoveragePrintTab")}
            </Tab>
            <Tab tabName={CCTabNames.EMAIL_MESSAGES} {...tabs}>
              <MailOutlineIcon className="mr-1 stroke-current" /> {t("hcpProfile.costAndCoverageEmailTab")}
            </Tab>
          </div>
          <TabPanel tabName={CCTabNames.PRINT_MESSAGES} {...tabs}>
            <CCTable key="costAndCoveragePrintMessages" columns={columns} data={data} isLoading={isLoading} />
          </TabPanel>
          <TabPanel tabName={CCTabNames.EMAIL_MESSAGES} {...tabs}>
            <CCTable key="costAndCoverageEmailMessages" columns={columns} data={data} isLoading={isLoading} />
          </TabPanel>
        </>
      )}
      {!showTabs && <CCTable columns={columns} data={data} isLoading={isLoading} />}
    </div>
  );
};

export default CostAndCoverageMessages;
